import { useEffect, useRef, useState } from "react";
import { apiClient } from "@/services/apiClient";

export interface Message {
  guid: string;
  messagesIcon: string;
  messagesTitle: string;
  message: string;
  status: string;
  createDt: string;
}

/**
 * useNotifications — polls for new messages tied to a sessionGuid.
 * Starts frequent (15s), doubles delay each cycle up to 2min if no new messages,
 * resets delay when new messages arrive.
 */
export function useNotifications(sessionGuid: string | null) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newCount, setNewCount] = useState(0);
  const delayRef = useRef(15000);
  const MAX_DELAY = 120000;

  useEffect(() => {
    if (!sessionGuid) return;

    let stopped = false;
    let previousCount = 0;

    const poll = async () => {
      try {
        const res = await apiClient.get<Message[]>(`/api/activitylog/session/${sessionGuid}`);
        const data = res.data;

        if (stopped) return;

        // detect new messages
        const hasNew = data.length > previousCount;
        previousCount = data.length;
        setMessages(data);
        setNewCount(hasNew ? data.length - messages.length : 0);

        // backoff adjustment
        delayRef.current = hasNew ? 15000 : Math.min(delayRef.current * 2, MAX_DELAY);
      } catch (err) {
        console.warn("Notification polling error:", err);
      } finally {
        if (!stopped) setTimeout(poll, delayRef.current);
      }
    };

    poll();
    return () => {
      stopped = true;
    };
  }, [sessionGuid]);

  return { messages, newCount };
}